import { z } from "zod/v4";
import {
	clubDetailSchema,
	communityCreationSchema,
	createClubSchema,
	type updateClubSchema,
} from "../schemas/club";
import {
	clubSquadMembersSchema,
	clubSquadSchema,
	clubSquadSessionSchema,
} from "../schemas/training";
import { fetchApi, fetchUpload, fetchVoid } from "./internal";

const IDEMPOTENCY_KEY_HEADER = "Idempotency-Key";

declare const clubCreationKeyBrand: unique symbol;

export type ClubCreationKey = string & {
	readonly [clubCreationKeyBrand]: "ClubCreationKey";
};

type CreateClub = z.infer<typeof createClubSchema>;

export type KeyedCreateClubCommand = Readonly<{
	idempotencyKey: ClubCreationKey;
	data: CreateClub;
}>;

export type CreateClubCommand =
	| KeyedCreateClubCommand
	| Readonly<{
			idempotencyKey?: never;
			data: CreateClub;
	  }>;

const keyedCreateClubCommandSchema = z.object({
	data: createClubSchema,
	idempotencyKey: z.string().uuid(),
});

export function newClubCreationCommand(data: CreateClub): KeyedCreateClubCommand {
	return Object.freeze({
		data: Object.freeze({ ...data }),
		idempotencyKey: crypto.randomUUID() as ClubCreationKey,
	});
}

export function parseClubCreationCommand(value: unknown): KeyedCreateClubCommand | null {
	const parsed = keyedCreateClubCommandSchema.safeParse(value);
	if (!parsed.success) return null;

	return Object.freeze({
		data: Object.freeze({ ...parsed.data.data }),
		idempotencyKey: parsed.data.idempotencyKey as ClubCreationKey,
	});
}

function creationHeaders(idempotencyKey: ClubCreationKey | undefined): HeadersInit | undefined {
	if (idempotencyKey === undefined) {
		return undefined;
	}

	return { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey };
}

export const club = {
	create: ({ data, idempotencyKey }: CreateClubCommand) =>
		fetchApi("/clubs", communityCreationSchema, {
			body: JSON.stringify(data),
			headers: creationHeaders(idempotencyKey),
			method: "POST",
		}),
	delete: (id: string) => fetchVoid(`/clubs/${id}`, { method: "DELETE" }),
	get: (id: string) => fetchApi(`/clubs/${id}`, clubDetailSchema),
	newCreationCommand: newClubCreationCommand,
	parseCreationCommand: parseClubCreationCommand,
	startTrial: (id: string, tier: "club" | "club_pro") =>
		fetchApi(`/clubs/${id}/trial`, clubDetailSchema, {
			body: JSON.stringify({ tier }),
			method: "POST",
		}),
	update: (id: string, data: z.infer<typeof updateClubSchema>) =>
		fetchApi(`/clubs/${id}`, clubDetailSchema, {
			body: JSON.stringify(data),
			method: "PUT",
		}),
	uploadAvatar: (id: string, file: File) => {
		const formData = new FormData();
		formData.append("avatar", file);
		return fetchUpload(`/clubs/${id}/avatar`, clubDetailSchema, formData);
	},
	uploadBanner: (id: string, file: File) => {
		const formData = new FormData();
		formData.append("banner", file);
		return fetchUpload(`/clubs/${id}/banner`, clubDetailSchema, formData);
	},
};

export const squads = {
	list: (clubId: string) => fetchApi(`/clubs/${clubId}/squads`, z.array(clubSquadSchema)),
	members: (squadId: string) =>
		fetchApi(`/clubs/${squadId}/members`, clubSquadMembersSchema),
	sessions: (squadId: string) =>
		fetchApi(`/clubs/${squadId}/sessions`, z.array(clubSquadSessionSchema)),
};
